import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles } from 'lucide-react';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { useJoyrideContext } from '../../contexts/JoyrideContext';
import { AI_ASSISTANT_FALLBACK, format } from './content.fallback';
import {
    getAiAssistantBackgroundRun,
    abortAiAssistant,
    askAiAssistantStream,
    getProject,
    downloadAiAssistantFile,
    clearAiAssistantSession,
    listAiAssistantConversations,
    getAiAssistantConversation,
    deleteAiAssistantConversation,
    getFeatureOrgUsage,
} from '../../utils/APIs';
import {
    DEFAULT_CONNECTORS,
    getUserIdFromToken,
    getStoredConnectors,
    getStoredProject,
    saveConnectors,
    saveProject,
} from '../../utils/aiAssistantStorage';
import ChatPanel from './ChatPanel';
import './AiAssistant.css';

/**
 * crypto.randomUUID needs a secure context, which a plain-HTTP dev host isn't,
 * so fall back rather than throw. The value only has to be distinct per
 * conversation — it is mixed into a server-side HMAC and never trusted on its
 * own, so it carries no security weight of its own.
 */
const newConversationId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Floating AI assistant, mounted once for the whole app.
 *
 * Connects to MCP connectors (Adobe Target, Adobe Analytics, Tealium)
 * and allows users to toggle active connectors.
 */
const AiAssistant = () => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const copy = content?.AI_ASSISTANT_CONTENT || AI_ASSISTANT_FALLBACK;
    const connectors = content?.AI_ASSISTANT_CONTENT?.connectors || AI_ASSISTANT_FALLBACK.connectors;

    const token = userData?.token;
    const organizationId = userData?.selectedOrganization?.org_token;
    // Stable per-user identifier derived from the JWT sub claim.
    // Used to namespace localStorage keys so different users on the same
    // browser don't share each other's selections.
    const userId = getUserIdFromToken(token);
    const { state: joyRideState, nextStep } = useJoyrideContext();
    const tourOpenedRef = useRef(false);

    const [isOpen, setIsOpen] = useState(false);
    const [projectSelection, setProjectSelection] = useState(null);
    const projectSelectionRef = useRef(null);
    const historyRequestRef = useRef(0);
    // Start with defaults. Because the JWT token (and therefore userId) is
    // resolved asynchronously by Auth0, we cannot read localStorage in a lazy
    // useState initializer — userId is null on the first render. Stored values
    // are applied by the restore effect below once userId becomes available.
    const defaultConnectors = connectors?.[0]?.id ? [connectors[0].id] : DEFAULT_CONNECTORS;
    const [connectorId, setConnectorId] = useState(defaultConnectors[0] || null);
    const [selectedConnectors, setSelectedConnectors] = useState(defaultConnectors);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    // Composer model selection; defaults to index 0 of fetched models.
    const [selectedModel, setSelectedModel] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [checkingBackground, setCheckingBackground] = useState(false);
    const stoppedRunRef = useRef(null);
    const [waitingForOutput, setWaitingForOutput] = useState(false);

    const hasRestoredRef = useRef(false);
    const [restoredProject, setRestoredProject] = useState(undefined);

    // Restore persisted connector + project selections once userId resolves.
    // Guarded by a ref so it only fires once per session and never clobbers a
    // project loaded later via an assistant-result event.
    useEffect(() => {
        if (!userId) return;
        if (hasRestoredRef.current) return;
        hasRestoredRef.current = true;

        const savedConnectors = getStoredConnectors(userId);
        if (savedConnectors) {
            setSelectedConnectors(savedConnectors);
            // Derive connectorId from the restored array — no need to store it separately.
            setConnectorId(savedConnectors.length === 1 ? savedConnectors[0] : savedConnectors.join(','));
        }

        const savedProject = getStoredProject(userId);
        if (savedProject) {
            setRestoredProject({ id: savedProject.id, version: Date.now() });
        }
    }, [userId]);


    // Persist connector selection — connectorId is always derivable from the array.
    useEffect(() => {
        saveConnectors(userId, selectedConnectors);
    }, [selectedConnectors, userId]);

    // Persist the selected project (id + name). Only save when it belongs to the
    // current org; clear when no project is selected.
    useEffect(() => {
        if (!userId || !projectSelection || projectSelection.organizationId !== organizationId) return;
        const project = projectSelection?.id && projectSelection.organizationId === organizationId
            ? { id: projectSelection.id, name: projectSelection.name }
            : null;
        saveProject(userId, project);
    }, [projectSelection, userId, organizationId]);

    const [showHistory, setShowHistory] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [conversationsError, setConversationsError] = useState(null);
    const [deletingConversationId, setDeletingConversationId] = useState(null);

    // AI credit eligibility: null = loading, true = has credits, false = no credits in plan
    const [aiAccessible, setAiAccessible] = useState(null);

    const messageIdRef = useRef(0);
    const abortControllerRef = useRef(null);
    const lastUserMessageRef = useRef('');
    const nextId = () => {
        messageIdRef.current += 1;
        return messageIdRef.current;
    };

    // Groups turns into one conversation so follow-ups keep context. It is not
    // the session key itself — the server derives that from the verified org
    // and user, and this only selects which of *their* conversations to use.
    // Rotating it is what makes Clear start a genuinely fresh thread.
    const conversationIdRef = useRef(newConversationId());

    // The Adobe IO integration picked in the global selector — the assistant
    // answers against whichever one the user currently has selected.
    const adobeIoId = userData?.defaultAdobeIO?._id;
    // Same idea for Tealium, from its own entry in the global selector. The
    // server uses whichever of the two the selected product needs.
    const tealiumKeyId = userData?.defaultTealiumKey?._id;

    // Check AI credit eligibility when the panel opens or the organization changes.
    useEffect(() => {
        if (!isOpen || !token || !organizationId) return;
        let cancelled = false;
        setAiAccessible(null); // loading
        getFeatureOrgUsage(token, organizationId, 'ai_credits')
            .then((result) => {
                if (cancelled) return;
                const data = result?.data;
                const limit = data?.max_allowed_usage ?? 0;
                const topup = data?.topup_credits ?? 0;
                // -1 means unlimited, any positive means included
                setAiAccessible(limit === -1 || limit > 0 || topup > 0);
            })
            .catch(() => {
                if (!cancelled) setAiAccessible(true); // fail-open: don't block on network error
            });
        return () => { cancelled = true; };
    }, [isOpen, token, organizationId]);

    // Synchronize open/closed state of the assistant panel during AI_ASSISTANT_TOUR
    useEffect(() => {
        if (joyRideState?.currentTour === 'AI_ASSISTANT_TOUR') {
            if (joyRideState.tourActive) {
                if (joyRideState.stepIndex >= 1) {
                    tourOpenedRef.current = true;
                    setIsOpen(true);
                }
            } else if (tourOpenedRef.current) {
                tourOpenedRef.current = false;
                setIsOpen(false);
            }
        }
    }, [joyRideState?.currentTour, joyRideState?.tourActive, joyRideState?.stepIndex]);

    const send = useCallback(
        async (rawText) => {
            const text = rawText.trim();
            if (!text || isSending || checkingBackground) return;

            stoppedRunRef.current = null;
            lastUserMessageRef.current = text;
            const assistantMsgId = nextId();
            setMessages((prev) => [
                ...prev,
                { id: nextId(), role: 'user', content: text },
                {
                    id: assistantMsgId,
                    role: 'assistant',
                    content: '',
                    files: [],
                    steps: [],
                    currentStatus: 'Thinking…',
                },
            ]);
            setInputValue('');
            setIsSending(true);
            setWaitingForOutput(false);
            const waitTimer = setTimeout(() => setWaitingForOutput(true), 60000);

            const controller = new AbortController();
            abortControllerRef.current = controller;

            const effectiveConnector = selectedConnectors.length === 1
                ? selectedConnectors[0]
                : selectedConnectors.join(',');

            try {
                await askAiAssistantStream({
                    token,
                    organizationId,
                    message: text,
                    adobe_io_id: adobeIoId,
                    conversation_id: conversationIdRef.current,
                    connector: effectiveConnector,
                    connectors: selectedConnectors,
                    project_id:
                        projectSelection?.organizationId === organizationId
                            ? projectSelection.id
                            : null,
                    tealium_key_id: tealiumKeyId,
                    model: selectedModel,
                    signal: controller.signal,
                    onStatus: (status) => {
                        if (controller.signal.aborted) return;
                        setWaitingForOutput(false);
                        setMessages((prev) =>
                            prev.map((msg) => {
                                if (msg.id !== assistantMsgId) return msg;
                                const prevSteps = Array.isArray(msg.steps) ? msg.steps : [];
                                const newSteps =
                                    prevSteps.length && prevSteps[prevSteps.length - 1] === status
                                        ? prevSteps
                                        : [...prevSteps, status];
                                return {
                                    ...msg,
                                    currentStatus: status,
                                    steps: newSteps,
                                };
                            })
                        );
                    },
                    onDelta: (chunk) => {
                        if (controller.signal.aborted) return;
                        setWaitingForOutput(false);
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === assistantMsgId
                                    ? {
                                          ...msg,
                                          content: msg.content + chunk,
                                          currentStatus: null,
                                      }
                                    : msg
                            )
                        );
                    },
                    onDone: (data) => {
                        if (controller.signal.aborted) return;
                        setWaitingForOutput(false);
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === assistantMsgId
                                    ? {
                                          ...msg,
                                          content: msg.content && msg.content.trim() ? msg.content : (data.answer || ''),
                                          files: data.files || [],
                                          isTruncated: Boolean(data.isTruncated || data.finishReason === 'length'),
                                          currentStatus: null,
                                      }
                                    : msg
                            )
                        );
                    },
                    onError: (err) => {
                        if (controller.signal.aborted) return;
                        setMessages((prev) =>
                            prev.map((msg) =>
                                msg.id === assistantMsgId
                                    ? {
                                          ...msg,
                                          role: msg.content ? 'assistant' : err?.status === 402 ? 'credit_error' : 'error',
                                          content:
                                              msg.content ||
                                              err?.message ||
                                              copy.errors.request_failed,
                                          errorCode: msg.content ? undefined : (err?.errorCode || 'unknown'),
                                          currentStatus: null,
                                      }
                                    : msg
                            )
                        );
                    },
                });
            } catch (error) {
                if (controller.signal.aborted) return;
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.id === assistantMsgId
                            ? {
                                  ...msg,
                                  role: msg.content ? 'assistant' : error?.status === 402 ? 'credit_error' : 'error',
                                  content:
                                      msg.content ||
                                      error?.message ||
                                      copy.errors.request_failed,
                                  errorCode: msg.content ? undefined : (error?.errorCode || 'unknown'),
                                  currentStatus: null,
                              }
                            : msg
                    )
                );
            } finally {
                clearTimeout(waitTimer);
                // A stopped request may finish after the next request starts.
                if (abortControllerRef.current === controller) {
                    setIsSending(false);
                    abortControllerRef.current = null;
                }
            }
        },
        [token, organizationId, adobeIoId, tealiumKeyId, connectorId, selectedConnectors, projectSelection, isSending, checkingBackground, copy, selectedModel]
    );

    // Reattach to project work after opening the panel or restoring its project.
    // Local streaming requests own their UI; polling only owns recovered runs.
    const recoveredRunRef = useRef(null);
    const selectedScopeId = projectSelection?.organizationId === organizationId ? projectSelection.id : undefined;
    useEffect(() => {
        if (!isOpen || !token || !organizationId || selectedScopeId === undefined) return;
        let cancelled = false;
        let timer;
        let conversationId;
        setCheckingBackground(true);
        const check = async () => {
            try {
                const result = await getAiAssistantBackgroundRun(token, organizationId, selectedScopeId, conversationId);
                if (cancelled || abortControllerRef.current) return;
                const run = result?.data;
                if (run && stoppedRunRef.current === run.conversation_id) return;
                if (!run) {
                    if (!conversationId) {
                        setMessages([]);
                        setIsSending(false);
                        conversationIdRef.current = newConversationId();
                    }
                    if (conversationId) {
                        setIsSending(false);
                        setMessages((items) => items.map((item, index) => index === items.length - 1
                            ? { ...item, currentStatus: null, content: item.content || 'Background request is no longer available. Please try again.' } : item));
                    }
                    recoveredRunRef.current = null;
                    return;
                }
                if (conversationId && recoveredRunRef.current !== conversationId) return;
                conversationId = run.conversation_id;
                conversationIdRef.current = conversationId;
                recoveredRunRef.current = conversationId;
                lastUserMessageRef.current = [...run.messages].reverse().find((item) => item.role === 'user')?.content || '';
                setMessages(run.messages.map((item) => ({ ...item, id: nextId() })));
                setShowHistory(false);
                if (run.connector) {
                    setConnectorId(run.connector);
                    setSelectedConnectors((current) => current.join(',') === run.connector ? current : run.connector.split(','));
                }
                const running = run.state === 'running';
                setIsSending(running);
                if (running) timer = setTimeout(check, 1500);
                else recoveredRunRef.current = null;
            } catch {
                // A temporary connection failure must not discard a running job.
                if (!cancelled) timer = setTimeout(check, 3000);
            } finally {
                if (!cancelled) setCheckingBackground(false);
            }
        };
        check();
        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (recoveredRunRef.current) setIsSending(false);
            recoveredRunRef.current = null;
            setCheckingBackground(false);
        };
    }, [isOpen, token, organizationId, selectedScopeId]);

    const handleRetry = useCallback(() => {
        const lastMsg = lastUserMessageRef.current;
        if (!lastMsg || isSending) return;
        // Strip the last user + assistant/error pair before re-sending.
        setMessages((prev) => {
            const copy = [...prev];
            // Remove trailing error/assistant message.
            if (copy.length && (copy[copy.length - 1].role === 'error' || copy[copy.length - 1].role === 'assistant')) {
                copy.pop();
            }
            // Remove the user message that preceded it.
            if (copy.length && copy[copy.length - 1].role === 'user') {
                copy.pop();
            }
            return copy;
        });
        send(lastMsg);
    }, [isSending, send]);

    const handleStop = useCallback(() => {
        stoppedRunRef.current = conversationIdRef.current;
        recoveredRunRef.current = null;
        // Best effort — the request may have already completed.
        abortAiAssistant(token, organizationId, conversationIdRef.current).catch(() => {});
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setMessages((prev) => {
            if (!prev.length) return prev;
            const last = prev[prev.length - 1];
            if (last.role === 'assistant') {
                return prev.map((m, idx) =>
                    idx === prev.length - 1
                        ? { ...m, role: 'aborted', content: m.content || 'Generation stopped.', currentStatus: null }
                        : m
                );
            }
            return prev;
        });
        setIsSending(false);
        setWaitingForOutput(false);
    }, [token, organizationId]);

    useEffect(() => {
        let cancelled = false;
        const openResult = async (event) => {
            if (isSending) return;
            const params = new URL(event.detail, window.location.origin).searchParams;
            setIsOpen(true);
            if (params.get('failed')) {
                setWaitingForOutput(false);
                setMessages([{ id: nextId(), role: 'error', content: 'Output generation failed. Please try again.' }]);
                return;
            }
            try {
                const projectId = params.get('project_id');
                const [result, projectResult] = await Promise.all([
                    getAiAssistantConversation(token, organizationId, params.get('conversation_id'), projectId),
                    projectId ? getProject(token, organizationId, projectId) : Promise.resolve(null),
                ]);
                if (cancelled) return;
                const project = projectResult?.data;
                const selection = { id: projectId, name: project?.name || '', prompts: project?.prompts || [], organizationId };
                projectSelectionRef.current = selection;
                setProjectSelection(selection);
                setRestoredProject({ id: projectId, version: Date.now() });
                conversationIdRef.current = result.data.conversation_id;
                const resConnector = result.data.connector;
                setConnectorId(resConnector);
                if (resConnector?.includes(',')) {
                    setSelectedConnectors(resConnector.split(',').map((p) => p.trim()).filter(Boolean));
                } else if (resConnector) {
                    setSelectedConnectors([resConnector]);
                }
                setMessages(result.data.messages.map((message) => ({ ...message, id: nextId() })));
                setInputValue('');
                setShowHistory(false);
                setWaitingForOutput(false);
            } catch (error) {
                if (!cancelled) setMessages([{ id: nextId(), role: 'error', content: error?.message || 'This output or its project is no longer available.' }]);
            }
        };
        window.addEventListener('assistant-result', openResult);
        return () => { cancelled = true; window.removeEventListener('assistant-result', openResult); };
    }, [token, organizationId, isSending]);

    const handleSubmit = useCallback(() => send(inputValue), [send, inputValue]);


    const handleClear = useCallback(() => {
        const previous = conversationIdRef.current;

        // Rotate first so the panel is usable immediately — the next question
        // starts a new conversation whether or not the cleanup call succeeds.
        conversationIdRef.current = newConversationId();
        setMessages([]);
        setInputValue('');

        // Best effort: releases the old transcript on the server instead of
        // leaving it to expire. A failure here changes nothing the user sees.
        clearAiAssistantSession(token, organizationId, previous).catch(() => {});
    }, [token, organizationId]);

    const handleSelectProject = useCallback((id, name = '', prompts = []) => {
        const current = projectSelectionRef.current;
        const sameProject = current?.id === id && current?.organizationId === organizationId;
        if (sameProject && current?.name === name && JSON.stringify(current?.prompts) === JSON.stringify(prompts)) return;
        projectSelectionRef.current = { id, name, prompts, organizationId };
        setProjectSelection({ id, name, prompts, organizationId });
        if (sameProject) return;
        historyRequestRef.current += 1;
        setConversations([]);
        setConversationsError(null);
        setConversationsLoading(false);
        setShowHistory(false);
        // A different project's context starts a fresh conversation.
        conversationIdRef.current = newConversationId();
        setMessages([]);
        setInputValue('');
    }, [organizationId]);

    const handleSelectConnector = useCallback(
        (nextId) => {
            const nextConnectors = nextId === 'hybrid'
                ? connectors.filter((item) => item.available === true).map((item) => item.id)
                : nextId ? [nextId] : defaultConnectors;
            if (!nextConnectors.length) return;
            const resolvedId = nextId === 'hybrid' ? nextConnectors.join(',') : nextId;
            if (resolvedId === connectorId) return;

            const previous = conversationIdRef.current;
            // A new thread per connector: answers come from different APIs,
            // so carrying one connector's transcript into another would give the
            // model contradictory context.
            conversationIdRef.current = newConversationId();
            setConnectorId(resolvedId);
            setSelectedConnectors(nextConnectors);
            setMessages([]);
            setInputValue('');

            if (messages.length) {
                clearAiAssistantSession(token, organizationId, previous).catch(
                    () => {}
                );
            }
        },
        [connectorId, connectors, messages.length, token, organizationId]
    );

    const handleToggleConnector = useCallback((idToToggle) => {
        setSelectedConnectors((prev) => {
            const isSelected = prev.includes(idToToggle);
            let next;
            if (isSelected) {
                if (prev.length <= 1) return prev; // Keep at least one active connector
                next = prev.filter((id) => id !== idToToggle);
            } else {
                next = [...prev, idToToggle];
            }

            if (next.length === 1) {
                setConnectorId(next[0]);
            } else {
                setConnectorId(next.join(','));
            }

            return next;
        });
    }, []);

    // Suggestions are a starting point, not a send. Clicking one loads it into
    // the composer so the user can edit it before submitting.
    const handlePromptClick = useCallback((prompt) => {
        setInputValue(prompt);
    }, []);

    const loadConversations = useCallback(async () => {
        const requestId = ++historyRequestRef.current;
        setConversationsLoading(true);
        setConversationsError(null);
        try {
            const result = await listAiAssistantConversations(token, organizationId, projectSelection?.organizationId === organizationId ? projectSelection.id : null);
            if (requestId !== historyRequestRef.current) return;
            setConversations(result?.data?.conversations || []);
        } catch (error) {
            if (requestId !== historyRequestRef.current) return;
            setConversationsError(
                error?.message || copy.errors.history_load_failed
            );
        } finally {
            if (requestId === historyRequestRef.current) setConversationsLoading(false);
        }
    }, [token, organizationId, projectSelection, copy]);

    const handleToggleHistory = useCallback(() => {
        setShowHistory((open) => {
            // Refetch on open rather than caching: the list changes every time
            // the user asks something, so a stale list is the common case.
            if (!open) loadConversations();
            return !open;
        });
    }, [loadConversations]);

    const handleSelectConversation = useCallback(
        async (id) => {
            const scope = projectSelectionRef.current;
            try {
                const result = await getAiAssistantConversation(
                    token,
                    organizationId,
                    id,
                    projectSelection?.organizationId === organizationId ? projectSelection.id : null
                );
                if (scope !== projectSelectionRef.current) return;
                const conversation = result?.data;
                if (!conversation) return;

                // Adopt the saved thread wholesale: its id, its connector, and its
                // transcript. The id is what lets the next question continue it
                // rather than start something new.
                conversationIdRef.current = conversation.conversation_id;
                const savedConnector = conversation.connector;
                if (savedConnector) {
                    setConnectorId(savedConnector);
                    if (savedConnector.includes(',')) {
                        setSelectedConnectors(savedConnector.split(',').map((p) => p.trim()).filter(Boolean));
                    } else {
                        setSelectedConnectors([savedConnector]);
                    }
                }

                setMessages(
                    (conversation.messages || []).map((message) => ({
                        id: nextId(),
                        role: message.role,
                        content: message.content,
                        files: message.files || [],
                    }))
                );
                setInputValue('');
                setShowHistory(false);
            } catch (error) {
                if (scope !== projectSelectionRef.current) return;
                setConversationsError(
                    error?.message || copy.errors.history_open_failed
                );
            }
        },
        [token, organizationId, projectSelection, copy]
    );

    const handleDeleteConversation = useCallback(
        async (id) => {
            setDeletingConversationId(id);
            const scope = projectSelectionRef.current;
            try {
                await deleteAiAssistantConversation(token, organizationId, id, projectSelection?.organizationId === organizationId ? projectSelection.id : null);
                if (scope !== projectSelectionRef.current) return;
                setConversations((prev) =>
                    prev.filter((item) => item.conversation_id !== id)
                );

                // Deleting the thread you're in leaves the panel showing a
                // transcript that no longer exists — start a fresh one.
                if (conversationIdRef.current === id) {
                    conversationIdRef.current = newConversationId();
                    setMessages([]);
                    setInputValue('');
                }
            } catch (error) {
                if (scope !== projectSelectionRef.current) return;
                setConversationsError(
                    error?.message || copy.errors.history_delete_failed
                );
            } finally {
                setDeletingConversationId(null);
            }
        },
        [token, organizationId, projectSelection, copy]
    );

    const handleDownload = useCallback(
        async (filename) => {
            try {
                await downloadAiAssistantFile(token, organizationId, filename);
            } catch (error) {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: nextId(),
                        role: 'error',
                        content:
                            error?.message ||
                            format(copy.errors.download_failed, { filename }),
                    },
                ]);
            }
        },
        [token, organizationId]
    );

    // Rendered into document.body rather than in place.
    //
    // Both the launcher and the panel are position:fixed. A fixed element is
    // only viewport-relative while no ancestor establishes a containing block —
    // and any ancestor with transform, filter, perspective, will-change or
    // contain does exactly that, which collapses the panel to zero height and
    // detaches it from the viewport. The app tree contains libraries that apply
    // those properties (react-joyride, react-toastify), so mounting on body
    // removes the possibility entirely instead of relying on no ancestor ever
    // gaining one.
    return createPortal(
        <>
            <button
                type="button"
                id="ai-assistant-launcher"
                className="sai-launcher"
                onClick={() => {
                    setIsOpen((open) => !open);
                    if (
                        joyRideState?.currentTour === 'AI_ASSISTANT_TOUR' &&
                        joyRideState?.tourActive &&
                        joyRideState?.stepIndex === 0
                    ) {
                        nextStep();
                    }
                }}
                aria-label={copy.launcher.aria_label}
                aria-expanded={isOpen}
            >
                <Sparkles size={22} aria-hidden="true" />
                {/* aria-hidden: the button already carries an aria-label, so
                    announcing this too would repeat it to screen readers. */}
                <span className="sai-launcher__tooltip" aria-hidden="true">
                    {copy.launcher.tooltip}
                </span>
            </button>

            <ChatPanel
                waitingForOutput={waitingForOutput}
                restoredProject={projectSelection?.organizationId === organizationId
                    ? { ...restoredProject, id: projectSelection.id }
                    : restoredProject}
                selectedProjectId={projectSelection?.organizationId === organizationId ? projectSelection.id : null}
                selectedProjectPrompts={projectSelection?.organizationId === organizationId ? projectSelection.prompts : undefined}
                selectedProjectName={projectSelection?.organizationId === organizationId ? projectSelection.name : ''}
                onSelectProject={handleSelectProject}
                token={token}
                organizationId={organizationId}
                isOpen={isOpen}
                copy={copy}
                connectors={connectors}
                connector={connectors.find((item) => item.id === connectorId) || (connectorId ? { id: connectorId, label: 'Connectors' } : null)}
                selectedConnectors={selectedConnectors}
                onToggleConnector={handleToggleConnector}
                messages={messages}
                inputValue={inputValue}
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
                isSending={isSending || checkingBackground}
                onClose={() => setIsOpen(false)}
                onClear={handleClear}
                onInputChange={setInputValue}
                onSubmit={handleSubmit}
                onStop={handleStop}
                onRetry={handleRetry}
                onPromptClick={handlePromptClick}
                onDownload={handleDownload}
                onSelectConnector={handleSelectConnector}
                showHistory={showHistory}
                onToggleHistory={handleToggleHistory}
                conversations={conversations}
                conversationsLoading={conversationsLoading}
                conversationsError={conversationsError}
                conversationId={conversationIdRef.current}
                onSelectConversation={handleSelectConversation}
                onDeleteConversation={handleDeleteConversation}
                deletingConversationId={deletingConversationId}
                aiAccessible={aiAccessible}
            />
        </>,
        document.body
    );
};

export default AiAssistant;
